import { postRequest } from '../../../shared/utils';

export type DeactivatePspParams = {
  screenId: number;
  schemaName: string;
  tableName: string;
  idsToDeactivate: number[];
};

/**
 * Deactivate existing active PSP records prior to saving the new record.
 *
 * Updates each provided id with:
 *  - active = 0
 *  - deactivation_date = today (UTC, YYYY-MM-DD)
 *  - update_date = now (UTC, ISO trimmed to seconds)
 *
 * @param {DeactivatePspParams} params
 * @param {number} params.screenId - Current screen grid id (used by tables API).
 * @param {string} params.schemaName - Database schema for the PSP table.
 * @param {string} params.tableName - PSP table name (e.g., 'psptable').
 * @param {number[]} params.idsToDeactivate - List of record ids to deactivate.
 * @returns {Promise<void>} Resolves when the API call succeeds or no ids provided.
 * @throws Propagates errors from the API call, wrapped with context.
 */
export async function deactivateExistingPspRecords(params: DeactivatePspParams): Promise<void> {
  const { screenId, schemaName, tableName, idsToDeactivate } = params;
  if (!idsToDeactivate?.length) return;

  const schema = schemaName?.trim();
  const table = tableName?.trim();
  if (!schema || !table) {
    throw new Error('schemaName and tableName are required');
  }

  // Validate and de-duplicate ids
  const ids = Array.from(
    new Set(
      idsToDeactivate.filter((n) => Number.isFinite(n) && n > 0).map((n) => Math.trunc(n))
    )
  );
  if (!ids.length) return;

  // Use a single "now" and keep UTC consistency for both fields
  const now = new Date();
  const updateIso = now.toISOString().slice(0, 19); // YYYY-MM-DDTHH:mm:ss (UTC, no Z)
  const deactivationDate = now.toISOString().slice(0, 10); // YYYY-MM-DD (UTC)

  const updateObjects = ids.map((id) => ({
    where: { id: { _eq: id } },
    _set: {
      active: 0,
      deactivation_date: deactivationDate,
      update_date: updateIso,
    },
  }));

  await postRequest('/api/tables', {
    screenGridId: screenId,
    tables: [
      {
        schema: schemaName,
        table: tableName,
        pkey: 'id',
        deleteIds: [],
        insertObjects: [],
        updateObjects,
      },
    ],
  });
}
